// Central GA4 event catalog: one params interface per event, mapped by event
// name in AnalyticsEventMap. Source of truth: the "Grova analytics" sheet
// (docs.google.com/spreadsheets/d/1bNsVXPp4umOH9bmIAqnh8l-RqWF-yOvbItGiT-iediw).
// Names follow GA4 rules (snake_case, ≤40 chars, no leading digit). Add new
// events here so call sites stay typo-proof.

import type {Locale} from '../shared/constants/locales';

// Where in the app the event fired. One canonical enum so call sites reference
// AnalyticsEntryPoint.X instead of scattering raw snake_case literals. Values
// are normalized to snake_case (the GA4 dimension) even where the sheet mixes
// formats ("country details page", "News listing").
export enum AnalyticsEntryPoint {
  Homepage = 'homepage',
  Header = 'header',
  Footer = 'footer',
  CountriesListingPage = 'countries listing page',
  CountryDetailsPage = 'country details page',
  AllProjectsListing = 'all_projects_listing',
  ProjectDetailsPage = 'project_details_page',
  PhasesListingPage = 'phases_listing_page',
  PhaseDetailsPage = 'phase_details_page',
  UnitTypesListingPage = 'unit_types_listing_page',
  UnitTypeDetailsPage = 'unit_type_details_page',
  Contact = 'contact',
  About = 'about',
  NewsListing = 'News listing',
  NewsDetailsPage = 'news',
  RegisterInterestForm = 'register interest form',
  NotFoundPage = '404 page',
}

// Carousel/section content kinds (sheet: "latest news", "unit types", …).
export const ANALYTICS_ITEM_TYPES = ['latest_news', 'unit_types', 'phases', 'amenities', 'nearby_locations'] as const;

export type AnalyticsItemType = (typeof ANALYTICS_ITEM_TYPES)[number];

// Required params are limited to what every call site always knows; context
// enrichment (section/project/phase ids, names) is optional — omit when the
// surface doesn't have it.
export interface BaseEventParams {
  entry_point: AnalyticsEntryPoint;
}

// Fired automatically by AnalyticsPageViews — not for manual call sites, so
// it does not extend BaseEventParams.
export interface PageViewParams {
  page_path: string;
  page_location: string;
  page_title: string;
}

// Sections / carousels
export interface ViewAllClickParams extends BaseEventParams {
  section_name?: string;
  section_id?: string;
  item_id?: string;
  item_name?: string;
  item_type?: AnalyticsItemType;
  project_id?: string;
  project_name?: string;
  phase_id?: string;
  phase_name?: string;
}

export interface ViewItemClickParams extends BaseEventParams {
  item_id: string;
  item_name: string;
  section_name?: string;
  section_id?: string;
  country_id?: string;
  country_name?: string;
  project_id?: string;
  project_name?: string;
  phase_id?: string;
  phase_name?: string;
}

export interface CardClickParams extends BaseEventParams {
  item_id: string;
  item_name: string;
  item_type?: AnalyticsItemType;
  section_name?: string;
  section_id?: string;
  project_id?: string;
  project_name?: string;
  phase_id?: string;
  phase_name?: string;
}

// Unit-type cards (sheet keeps this as its own event).
export interface ItemCardClickParams extends BaseEventParams {
  item_id: string;
  item_name: string;
  project_id?: string;
  project_name?: string;
  phase_id?: string;
  phase_name?: string;
}

export interface LearnMoreClickParams extends BaseEventParams {
  section_name?: string;
  section_id?: string;
  item_id?: string;
  item_name?: string;
  redirection_link?: string;
}

// Navigation chrome
export interface HeaderTabClickParams extends BaseEventParams {
  tab_name: string;
  tab_id?: string;
}

export interface FooterTabClickParams extends BaseEventParams {
  tab_name: string;
  tab_id?: string;
}

export interface UpdateLanguageParams extends BaseEventParams {
  selected_language: Locale;
  previously_selected_language: Locale;
}

export interface SocialMediaLinksClickParams extends BaseEventParams {
  item_type: 'facebook' | 'instagram' | 'linkedin';
}

// Detail-page interactions
export interface GalleryClickParams extends BaseEventParams {
  item_id?: string;
  item_name?: string;
}

export interface TakeATourParams extends BaseEventParams {
  item_id: string;
  item_name?: string;
}

export interface MapClickParams extends BaseEventParams {
  item_id: string;
  item_name?: string;
}

export interface MasterplanClickParams extends BaseEventParams {
  item_id: string;
  item_name?: string;
}

export interface FloorPlanClickParams extends BaseEventParams {
  item_id: string;
  item_name?: string;
}

export interface DownloadBrochureClickParams extends BaseEventParams {
  item_id: string;
  item_name?: string;
}

export interface LabelClickParams extends BaseEventParams {
  item_id: string;
  item_name: string;
}

// News
export interface LinkClickParams extends BaseEventParams {
  redirection_link: string;
  item_id?: string;
  item_name?: string;
}

export interface FilterClickParams extends BaseEventParams {
  category_name: string;
  previous_category_name?: string;
}

// Contact / conversion
export interface SubmitContactFormParams extends BaseEventParams {
  selected_inquiry_type: string;
  additional_info_added: 'yes' | 'no';
}

export interface ContactMethodClickParams extends BaseEventParams {
  item_type: 'hotline' | 'whatsapp' | 'email' | 'address';
}

export interface RegisterInterestClickParams extends BaseEventParams {
  section_name?: string;
  section_id?: string;
  item_id?: string;
  item_name?: string;
}

export interface SubmitRegisterInterestParams extends BaseEventParams {
  contact_method: 'whatsapp' | 'phone_call';
  preferred_time_to_contact?: string;
  item_id?: string;
  item_name?: string;
  item_type?: 'project' | 'property_type' | 'unit';
}

// Misc
export interface VideoUnmuteParams extends BaseEventParams {
  section_name: string;
  section_id?: string;
}

export interface BackToHomepageClickParams extends BaseEventParams {
  failed_url: string;
  previous_page?: string;
}

export interface AnalyticsEventMap {
  page_view: PageViewParams;
  view_all_click: ViewAllClickParams;
  view_item_click: ViewItemClickParams;
  card_click: CardClickParams;
  item_card_click: ItemCardClickParams;
  learn_more_click: LearnMoreClickParams;
  header_tab_click: HeaderTabClickParams;
  footer_tab_click: FooterTabClickParams;
  update_language: UpdateLanguageParams;
  social_media_links_click: SocialMediaLinksClickParams;
  gallery_click: GalleryClickParams;
  take_a_tour: TakeATourParams;
  map_click: MapClickParams;
  masterplan_click: MasterplanClickParams;
  floor_plan_click: FloorPlanClickParams;
  download_brochure_click: DownloadBrochureClickParams;
  label_click: LabelClickParams;
  link_click: LinkClickParams;
  filter_click: FilterClickParams;
  submit_contact_form: SubmitContactFormParams;
  contact_method_click: ContactMethodClickParams;
  register_interest_click: RegisterInterestClickParams;
  submit_register_interest: SubmitRegisterInterestParams;
  video_unmute: VideoUnmuteParams;
  back_to_homepage_click: BackToHomepageClickParams;
}

export type AnalyticsEventName = keyof AnalyticsEventMap;
